/**
 * Info bars, inline forms, and detail widgets for the Trace Studio panel:
 * asset/scene selection bars (with rename/delete), confirmation bars, the
 * create-scene form, QR codes, publish-status dropdown, and the login screen.
 */

import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { getAssetCategory } from 'trace-viewer/scene';
import type { ProjectAccessorResponseDto, SceneResponseDto, SceneAccessorResponseDto } from 'trace-viewer/cms';
import { contentDownloadUrl, Scenes } from 'trace-viewer/cms';
import { styles } from './catalog-styles';
import {
    BLOB_BASE_URL, apiConfig, resolveUrl, shareUrl, viewerUrl,
    normalizeStatus, PUBLISH_STATUS_LABELS, PUBLISH_STATUS_ORDER, type PublishStatus,
    type AssetItem, type AssetFileMetadata,
} from './catalog-api';
import { API_BASE_URL, LS_REFRESH_TOKEN_KEY, LS_EMAIL_KEY } from './catalog-auth';
import { CubeSvg } from './catalog-cards';

const ACTION_BTN_STYLE: React.CSSProperties = {
    padding: '4px 10px', fontSize: '11px', fontWeight: 600, lineHeight: '16px',
    color: 'var(--colorNeutralForegroundOnBrand)', backgroundColor: 'var(--colorBrandBackground)',
    border: 'none', borderRadius: '3px', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
};
const ACTION_BTN_SECONDARY: React.CSSProperties = {
    ...ACTION_BTN_STYLE,
    backgroundColor: 'var(--colorNeutralBackground3)',
    color: 'var(--colorNeutralForeground1)',
};

/** Extra CMS-only type labels not covered by the core asset categories. */
const CMS_TYPE_LABELS: Record<string, string> = {
    svg: 'Image', gif: 'Image', mov: 'Video',
    mp3: 'Audio', ogg: 'Audio', wav: 'Audio',
    hdr: 'Environment', env: 'Environment',
};

const CATEGORY_TO_LABEL: Record<string, string> = {
    model: 'Model', splat: 'Model', image: 'Image', video: 'Video', audio: 'Audio', scene: 'Scene',
};

/** Derive a human-readable type label from filename extension. */
function getAssetTypeLabel(asset: AssetItem): string {
    const ext = (asset.originalFilename?.match(/\.([^.]+)$/)?.[1] ?? asset.fileType ?? '').toLowerCase();
    const label = CATEGORY_TO_LABEL[getAssetCategory(ext)] ?? CMS_TYPE_LABELS[ext];
    return label ?? (ext ? ext.toUpperCase() : 'Asset');
}

/** Format a date string for display. */
function formatDate(dateStr: string | undefined | null): string | null {
    if (!dateStr) return null;
    try {
        return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return null; }
}

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

/** Format a byte count as a human-readable size (fallback for missing readableSize). */
function formatFileSize(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    let size = bytes;
    let unit = 0;
    while (size >= 1024 && unit < SIZE_UNITS.length - 1) {
        size /= 1024;
        unit++;
    }
    return `${unit === 0 ? size : size.toFixed(2)} ${SIZE_UNITS[unit]}`;
}

/** Format a duration in seconds as m:ss (or h:mm:ss). */
function formatDuration(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '';
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
    return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

/** Turn a camelCase / snake_case metadata key into a Title Case label. */
function humanizeKey(key: string): string {
    return key
        .replace(/([a-z\d])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase());
}

/** Render a metadata value as a short display string; null if not worth showing. */
function formatMetaValue(value: unknown): string | null {
    if (value == null) return null;
    if (typeof value === 'string') return value.trim() || null;
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return null; // skip nested objects/arrays
}

// ============================================================================
// Detail Helpers (shortcode, location, dates)
// ============================================================================

const DETAIL_STYLE: React.CSSProperties = {
    fontSize: '10px', color: 'var(--colorNeutralForeground4)', lineHeight: '14px',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

export const CODE_STYLE: React.CSSProperties = {
    fontFamily: "'SF Mono', 'Cascadia Code', 'Fira Code', 'JetBrains Mono', Consolas, monospace",
    letterSpacing: '0.02em',
};

/** Format a short code for display: insert dash in the middle for 6-char alphanumeric codes. */
export function formatCode(code: string): string {
    if (/^[A-Za-z0-9]{6}$/.test(code)) {
        return `${code.slice(0, 3)}-${code.slice(3)}`;
    }
    return code;
}

/** Short code rendered as a link to the public viewer, with a copy-code button. */
export const ShortCodeLink: React.FC<{ code: string }> = ({ code }) => {
    const [copied, setCopied] = useState(false);
    useEffect(() => {
        if (!copied) return;
        const t = setTimeout(() => setCopied(false), 1200);
        return () => clearTimeout(t);
    }, [copied]);
    const url = viewerUrl(code);
    const copy = () => {
        navigator.clipboard.writeText(code)
            .then(() => setCopied(true))
            .catch((err: unknown) => console.error('[ShortCodeLink] copy failed:', err));
    };
    return (
        <span title="Short code" style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${url}`}
                style={{ ...CODE_STYLE, color: 'var(--colorBrandForegroundLink)', textDecoration: 'none', userSelect: 'text' }}
                onMouseEnter={e => { (e.target as HTMLElement).style.textDecoration = 'underline'; }}
                onMouseLeave={e => { (e.target as HTMLElement).style.textDecoration = 'none'; }}
            >
                {formatCode(code)}
            </a>
            <button
                onClick={copy}
                title={copied ? 'Copied' : 'Copy code'}
                aria-label="Copy code"
                style={{
                    padding: '1px', border: 'none', background: 'none', cursor: 'pointer',
                    color: copied ? 'var(--colorBrandForegroundLink)' : 'inherit',
                    display: 'inline-flex', alignItems: 'center', lineHeight: 0,
                }}
            >
                {copied ? (
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
                    </svg>
                ) : (
                    <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M4 2a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2V2zm2-.5a.5.5 0 00-.5.5v8a.5.5 0 00.5.5h6a.5.5 0 00.5-.5V2a.5.5 0 00-.5-.5H6z"/>
                        <path d="M2 4.5a.5.5 0 00-.5.5v9a.5.5 0 00.5.5h7a.5.5 0 00.5-.5V13H11v1a2 2 0 01-2 2H2a2 2 0 01-2-2V5a2 2 0 012-2h1v1.5H2z"/>
                    </svg>
                )}
            </button>
        </span>
    );
};

/** Renders a QR code for the given URL into a canvas. */
export const QrCode: React.FC<{ url: string; size?: number; title?: string }> = ({ url, size = 58, title }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        QRCode.toCanvas(canvas, url, {
            width: size,
            margin: 1,
            color: { dark: '#000000', light: '#ffffff' },
        }).catch((err: unknown) => {
            console.error('[QrCode] render failed:', err);
        });
    }, [url, size]);
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={title ?? url}
            style={{
                display: 'inline-block', flexShrink: 0, lineHeight: 0,
                borderRadius: '3px', overflow: 'hidden', backgroundColor: '#ffffff',
                padding: '2px',
            }}
        >
            <canvas ref={canvasRef} width={size} height={size} style={{ display: 'block', width: size, height: size }} />
        </a>
    );
};

const LocationLink: React.FC<{ lat: number; lng: number; address?: string | null }> = ({ lat, lng, address }) => {
    const label = address ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    const query = address ? encodeURIComponent(address) : `${lat},${lng}`;
    return (
        <a
            href={`https://www.google.com/maps/search/?api=1&query=${query}`}
            target="_blank"
            rel="noopener noreferrer"
            title={`${lat.toFixed(5)}, ${lng.toFixed(5)}`}
            style={{ color: 'var(--colorBrandForegroundLink)', textDecoration: 'none' }}
            onMouseEnter={e => { (e.target as HTMLElement).style.textDecoration = 'underline'; }}
            onMouseLeave={e => { (e.target as HTMLElement).style.textDecoration = 'none'; }}
        >
            {label}
        </a>
    );
};

/** Extracts location from the first accessor that has lat/lng. */
function getAccessorLocation(accessors: (ProjectAccessorResponseDto | SceneAccessorResponseDto)[] | null | undefined): { lat: number; lng: number; address?: string | null } | null {
    if (!accessors) return null;
    for (const a of accessors) {
        if (a.latitude != null && a.longitude != null) {
            return { lat: a.latitude, lng: a.longitude, address: a.address };
        }
    }
    return null;
}

/** Created/Updated detail rows, used in both project header and scene info bar. */
export const DateRows: React.FC<{ dateCreated?: string | null; dateModified?: string | null }> = ({ dateCreated, dateModified }) => {
    const created = formatDate(dateCreated);
    const modified = formatDate(dateModified);
    if (!created && !modified) return null;
    return (
        <>
            {created && <div style={DETAIL_STYLE}>Created {created}</div>}
            {modified && <div style={DETAIL_STYLE}>Updated {modified}</div>}
        </>
    );
};

/** Detail rows for shortcode and location, used in both project header and scene info bar. */
export const EntityDetails: React.FC<{
    shortCode?: string | null;
    accessors?: (ProjectAccessorResponseDto | SceneAccessorResponseDto)[] | null;
}> = ({ shortCode, accessors }) => {
    const location = getAccessorLocation(accessors);
    if (!shortCode && !location) return null;
    return (
        <div style={{ ...DETAIL_STYLE, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {shortCode && <ShortCodeLink code={shortCode} />}
            {location && (
                <span>
                    <LocationLink lat={location.lat} lng={location.lng} address={location.address} />
                </span>
            )}
        </div>
    );
};

// ============================================================================
// Buttons
// ============================================================================

const OpenButton: React.FC<{ onClick: () => void; busy: boolean }> = ({ onClick, busy }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            style={{
                ...ACTION_BTN_STYLE,
                opacity: busy ? 0.5 : 1,
                backgroundColor: !busy && hovered ? 'var(--colorBrandBackgroundHover)' : 'var(--colorBrandBackground)',
            }}
            onClick={onClick}
            disabled={busy}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            {busy ? 'Opening...' : 'Open'}
        </button>
    );
};

/**
 * Derive the file extension (lowercased, no dot) from a filename.
 * Returns undefined for names without a dotted extension so callers can
 * fall back to a generic "Download" label.
 */
function extractExt(name: string | null | undefined): string | undefined {
    if (!name) return undefined;
    const idx = name.lastIndexOf('.');
    if (idx < 0 || idx === name.length - 1) return undefined;
    return name.slice(idx + 1).toLowerCase();
}

/**
 * What the download link actually serves, and what the browser should call it.
 *
 * `originalFilename` is what was UPLOADED, and under the content model that is
 * no longer what comes back. A `.glz` bundle is the packed form of a folder
 * and the server unpacks it on import (spec §4), so the row's url is the
 * folder's `scene.<hash16>.gltf` **document** — one file of several. Labelling
 * that link `.glz` promises a self-contained archive, hands over a document,
 * and saves it under a name that lies about its bytes.
 *
 * So the url is the authority on the extension; the uploaded name only
 * supplies the human stem. They agree for every row whose bytes travel
 * unchanged — a legacy `.trace` blob, an unedited image leaf — and this is a
 * no-op there.
 */
export function assetDownloadName(asset: AssetItem, url: string): { ext?: string; filename?: string } {
    const served = extractExt(url.split(/[?#]/)[0].split('/').pop());
    const uploaded = extractExt(asset.originalFilename);
    if (!served || served === uploaded) {
        return { ext: uploaded ?? served, filename: asset.originalFilename ?? undefined };
    }
    const stem = (asset.originalFilename ?? asset.title ?? 'asset').replace(/\.[^/.]+$/, '');
    return { ext: served, filename: `${stem}.${served}` };
}

const DownloadLink: React.FC<{ url: string; ext?: string | null; filename?: string | null }> = ({ url, ext, filename }) => {
    const [hovered, setHovered] = useState(false);
    const label = ext ? `.${ext}` : 'Download';
    return (
        <a
            href={url}
            download={filename ?? true}
            title={`Download ${filename ?? label}`}
            style={{
                ...ACTION_BTN_SECONDARY,
                gap: '4px', textDecoration: 'none',
                backgroundColor: hovered ? 'var(--colorNeutralBackground3Hover)' : 'var(--colorNeutralBackground3)',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.75 14A1.75 1.75 0 011 12.25v-2.5a.75.75 0 011.5 0v2.5c0 .138.112.25.25.25h10.5a.25.25 0 00.25-.25v-2.5a.75.75 0 011.5 0v2.5A1.75 1.75 0 0113.25 14H2.75z"/>
                <path d="M7.25 7.689V2a.75.75 0 011.5 0v5.689l1.97-1.969a.749.749 0 111.06 1.06l-3.25 3.25a.749.749 0 01-1.06 0L4.22 6.78a.749.749 0 111.06-1.06l1.97 1.969z"/>
            </svg>
            {label}
        </a>
    );
};

export const DeleteButton: React.FC<{ onClick: () => void; busy: boolean; disabled: boolean; title?: string }> = ({ onClick, busy: _busy, disabled, title = 'Delete' }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            style={{
                marginLeft: 'auto',
                padding: '4px', background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer',
                color: !disabled && hovered ? 'var(--colorPaletteRedForeground1)' : 'var(--colorNeutralForeground4)',
                opacity: disabled ? 0.4 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '3px', transition: 'color 0.15s',
            }}
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={title}
        >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M6.5 1.75a.25.25 0 01.25-.25h2.5a.25.25 0 01.25.25V3h-3V1.75zM5 3V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75V3h3.25a.75.75 0 010 1.5h-.75v8.75A1.75 1.75 0 0111.75 15h-7.5A1.75 1.75 0 012.5 13.25V4.5h-.75a.75.75 0 010-1.5H5zM4 4.5v8.75c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25V4.5H4zM6.75 6a.75.75 0 01.75.75v5.5a.75.75 0 01-1.5 0v-5.5A.75.75 0 016.75 6zm3.25.75a.75.75 0 00-1.5 0v5.5a.75.75 0 001.5 0v-5.5z"/>
            </svg>
        </button>
    );
};

/** Small pencil icon button used for inline rename affordances. */
export const RenameButton: React.FC<{ onClick: () => void; disabled?: boolean; title?: string }> = ({ onClick, disabled, title = 'Rename' }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            style={{
                padding: '2px', background: 'none', border: 'none',
                cursor: disabled ? 'default' : 'pointer',
                color: !disabled && hovered ? 'var(--colorNeutralForeground1)' : 'var(--colorNeutralForeground4)',
                opacity: disabled ? 0.4 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '3px', transition: 'color 0.15s', flexShrink: 0,
            }}
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={title}
        >
            <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11.013 1.427a1.75 1.75 0 012.474 0l1.086 1.086a1.75 1.75 0 010 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 01-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61zm1.414 1.06a.25.25 0 00-.354 0L10.811 3.75l1.439 1.44 1.263-1.263a.25.25 0 000-.354l-1.086-1.086zM11.189 6.25L9.75 4.81l-6.286 6.287a.25.25 0 00-.064.108l-.558 1.953 1.953-.558a.249.249 0 00.108-.064l6.286-6.286z"/>
            </svg>
        </button>
    );
};

// ============================================================================
// Inline name editor
// ============================================================================

/**
 * Inline rename editor: replaces a title with an input; Enter commits,
 * Escape cancels. The caller owns the API call and busy state.
 */
export const InlineNameEdit: React.FC<{
    initial: string;
    busy?: boolean;
    onCommit: (name: string) => void;
    onCancel: () => void;
}> = ({ initial, busy, onCommit, onCancel }) => {
    const [draft, setDraft] = useState(initial);
    const commit = () => {
        const name = draft.trim();
        if (!name || name === initial) { onCancel(); return; }
        onCommit(name);
    };
    return (
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center', minWidth: 0 }}>
            <input
                type="text"
                value={draft}
                autoFocus
                disabled={busy}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter') commit();
                    else if (e.key === 'Escape') onCancel();
                }}
                style={{ ...styles.input, flex: 1, minWidth: 0, fontSize: '12px', padding: '2px 6px' }}
            />
            <button
                onClick={commit}
                disabled={busy || !draft.trim()}
                title="Save name (Enter)"
                style={{ ...ACTION_BTN_STYLE, padding: '2px 8px', opacity: busy || !draft.trim() ? 0.5 : 1 }}
            >
                Save
            </button>
            <button
                onClick={onCancel}
                disabled={busy}
                title="Cancel (Esc)"
                style={{ ...ACTION_BTN_SECONDARY, padding: '2px 8px', opacity: busy ? 0.5 : 1 }}
            >
                &times;
            </button>
        </div>
    );
};

// ============================================================================
// Info bars
// ============================================================================

/** Shared base info bar used by asset and scene selection detail panels. */
const EntityInfoBar: React.FC<{
    thumbnailUrl?: string | null;
    thumbnailAlt?: string;
    thumbnailFallback?: React.ReactNode;
    title: string;
    detailRows?: React.ReactNode;
    onDeselect: () => void;
    actions: React.ReactNode;
    trailing?: React.ReactNode;
    /** Optional inline rename (caller owns the API call). */
    onRename?: (name: string) => void;
    renaming?: boolean;
}> = ({ thumbnailUrl, thumbnailAlt = '', thumbnailFallback, title, detailRows, onDeselect, actions, trailing, onRename, renaming }) => {
    const [editing, setEditing] = useState(false);
    // Close the editor when the subject changes (e.g. another card selected).
    useEffect(() => { setEditing(false); }, [title]);
    return (
        <div style={{
            width: '100%', backgroundColor: 'var(--colorNeutralBackground2)', borderRadius: '4px',
            marginBottom: '6px', flexShrink: 0, overflow: 'hidden',
        }}>
            <div style={{ display: 'flex', gap: '8px', padding: '8px', alignItems: 'flex-start' }}>
                <div style={{ width: 58, height: 58, flexShrink: 0, borderRadius: '3px', overflow: 'hidden', backgroundColor: 'var(--colorNeutralBackground3)' }}>
                    {thumbnailUrl ? (
                        <img src={thumbnailUrl} alt={thumbnailAlt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} crossOrigin="anonymous" />
                    ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {thumbnailFallback}
                        </div>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0, fontSize: '10px', color: 'var(--colorNeutralForeground4)', lineHeight: '14px' }}>
                    {editing && onRename ? (
                        <InlineNameEdit
                            initial={title}
                            busy={renaming}
                            onCommit={name => { setEditing(false); onRename(name); }}
                            onCancel={() => setEditing(false)}
                        />
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0 }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--colorNeutralForeground1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '16px', minWidth: 0 }}>{title}</div>
                            {onRename && <RenameButton onClick={() => setEditing(true)} disabled={renaming} />}
                        </div>
                    )}
                    {detailRows}
                </div>
                {trailing}
                <button
                    onClick={onDeselect}
                    style={{ background: 'none', border: 'none', color: 'var(--colorNeutralForeground4)', cursor: 'pointer', padding: '0 2px', fontSize: '14px', lineHeight: 1, flexShrink: 0 }}
                    title="Deselect"
                >&times;</button>
            </div>
            <div style={{ display: 'flex', gap: '6px', padding: '0 8px 8px', alignItems: 'center' }}>
                {actions}
            </div>
        </div>
    );
};

/** Metadata keys surfaced elsewhere in the info bar (so the foldout doesn't repeat them). */
const HANDLED_META_KEYS = new Set(['size', 'readableSize']);

/** Collapsible list of any extra file metadata beyond size (e.g. duration, codec). */
const AssetMetadataFoldout: React.FC<{ meta: AssetFileMetadata }> = ({ meta }) => {
    const [expanded, setExpanded] = useState(false);
    const rows: Array<{ label: string; value: string }> = [];
    for (const [key, raw] of Object.entries(meta)) {
        if (HANDLED_META_KEYS.has(key)) continue;
        const value = key === 'duration' && typeof raw === 'number' ? formatDuration(raw) : formatMetaValue(raw);
        if (value) rows.push({ label: humanizeKey(key), value });
    }
    if (rows.length === 0) return null;

    return (
        <div style={{ marginTop: '2px' }}>
            <div
                onClick={() => setExpanded(e => !e)}
                style={{ ...DETAIL_STYLE, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' }}
            >
                <span style={{ display: 'inline-block', transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: '7px' }}>▶</span>
                Metadata
            </div>
            {expanded && (
                <div style={{ paddingLeft: '11px' }}>
                    {rows.map(row => (
                        <div key={row.label} style={{ ...DETAIL_STYLE, display: 'flex', gap: '4px' }}>
                            <span style={{ color: 'var(--colorNeutralForeground3)' }}>{row.label}:</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.value}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/** Info bar shown above the asset grid when an asset is selected. */
export const AssetInfoBar: React.FC<{
    asset: AssetItem;
    onImport: () => void;
    onOpen: () => void;
    onDeselect: () => void;
    busy: 'import' | 'open' | false;
    onDelete?: () => void;
    deleting?: boolean;
}> = ({ asset, onImport, onOpen, onDeselect, busy, onDelete, deleting }) => {
    const name = asset.title ?? asset.originalFilename ?? 'Asset';
    const modified = formatDate(asset.dateModified);
    const created = formatDate(asset.dateCreated);
    const downloadUrl = resolveUrl(asset.url);
    const download = downloadUrl ? assetDownloadName(asset, downloadUrl) : undefined;

    const meta = asset.fileMetadata;
    const sizeBytes = typeof meta?.size === 'number' ? meta.size : undefined;
    const sizeLabel = meta?.readableSize?.trim() || (sizeBytes != null ? formatFileSize(sizeBytes) : null);

    return (
        <EntityInfoBar
            thumbnailUrl={resolveUrl(asset.thumbnailUrl)}
            thumbnailAlt={name}
            thumbnailFallback={<CubeSvg size={20} />}
            title={name}
            detailRows={<>
                <div>{getAssetTypeLabel(asset)}{sizeLabel && <> · <span title={sizeBytes != null ? `${sizeBytes.toLocaleString()} bytes` : undefined}>{sizeLabel}</span></>}</div>
                {asset.creator?.email && <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.creator.email}</div>}
                {modified ? <div>Modified {modified}</div> : created ? <div>Created {created}</div> : null}
                {meta && <AssetMetadataFoldout meta={meta} />}
            </>}
            onDeselect={onDeselect}
            actions={<>
                <button
                    style={{ ...ACTION_BTN_STYLE, gap: '3px', opacity: busy ? 0.5 : 1 }}
                    onClick={onImport}
                    disabled={!!busy}
                >
                    {busy === 'import' ? 'Importing...' : <><svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z"/></svg>Import</>}
                </button>
                <OpenButton onClick={onOpen} busy={!!busy} />
                {downloadUrl && <DownloadLink url={downloadUrl} ext={download?.ext} filename={download?.filename} />}
                {onDelete && <DeleteButton onClick={onDelete} busy={!!deleting} disabled={!!busy || !!deleting} title="Delete asset" />}
            </>}
        />
    );
};

/** Info bar shown above the scene grid when a scene is selected. */
export const SceneInfoBar: React.FC<{
    scene: SceneResponseDto;
    token: string;
    onOpen: () => void;
    onDeselect: () => void;
    busy: boolean;
    /** Optional publish dropdown (caller owns state + API call). */
    publishControl?: React.ReactNode;
    /** Optional rename (caller owns the API call). */
    onRename?: (name: string) => void;
    renaming?: boolean;
    /** Optional delete request (caller shows the confirm bar). */
    onDelete?: () => void;
    deleting?: boolean;
}> = ({ scene, token, onOpen, onDeselect, busy, publishControl, onRename, renaming, onDelete, deleting }) => {
    const name = scene.name ?? 'Scene';

    // Derive label/extension from the stored RootContent filename so a Scene
    // whose content is `.glxf` doesn't show ".zip" (and vice-versa). Falls
    // back to undefined → DownloadLink renders the generic "Download" label.
    const [downloadState, setDownloadState] = useState<{ status: 'loading' | 'ready' | 'none'; url?: string; ext?: string }>({ status: 'loading' });
    useEffect(() => {
        if (!scene.id) { setDownloadState({ status: 'none' }); return; }
        setDownloadState({ status: 'loading' });
        let cancelled = false;
        void (async () => {
            try {
                const scenesClient = new Scenes(apiConfig(token));
                const res = await scenesClient.contentsList(scene.id!);
                const contents = res.data?.contents ?? [];
                const root = contents.find(c => c.contentType === 'RootContent');
                if (cancelled) return;
                if (root?.id) {
                    const url = contentDownloadUrl(BLOB_BASE_URL, { id: root.id, contentType: root.contentType ?? null, name: root.name });
                    setDownloadState({ status: 'ready', url, ext: extractExt(root.name) });
                } else {
                    setDownloadState({ status: 'none' });
                }
            } catch {
                if (!cancelled) setDownloadState({ status: 'none' });
            }
        })();
        return () => { cancelled = true; };
    }, [scene.id, token]);

    return (
        <EntityInfoBar
            thumbnailUrl={resolveUrl(scene.imageUrl)}
            thumbnailAlt={name}
            thumbnailFallback={<CubeSvg size={20} />}
            title={name}
            onRename={onRename}
            renaming={renaming}
            detailRows={<>
                <div>Scene{scene.shortCode ? <>{' · '}<ShortCodeLink code={scene.shortCode} /></> : ''}</div>
                <DateRows dateCreated={scene.dateCreated} dateModified={scene.dateModified} />
                <EntityDetails accessors={scene.sceneAccessors} shortCode={null} />
            </>}
            trailing={scene.shortCode ? <QrCode url={shareUrl('scenes', scene.shortCode)} title={`Open ${shareUrl('scenes', scene.shortCode)}`} /> : undefined}
            onDeselect={onDeselect}
            actions={<>
                <OpenButton onClick={onOpen} busy={busy} />
                {downloadState.status === 'loading' && (
                    <button style={{ ...ACTION_BTN_SECONDARY, gap: '4px', opacity: 0.5, cursor: 'default' }} disabled aria-label="Loading download…">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                            <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8z" opacity=".2"/>
                            <path d="M8 0a8 8 0 018 8h-1.5A6.5 6.5 0 008 1.5V0z"/>
                        </svg>
                    </button>
                )}
                {downloadState.status === 'ready' && downloadState.url && (
                    <DownloadLink url={downloadState.url} ext={downloadState.ext} />
                )}
                {publishControl && <span style={{ marginLeft: 'auto' }}>{publishControl}</span>}
                {onDelete && (
                    <span style={publishControl ? undefined : { marginLeft: 'auto' }}>
                        <DeleteButton onClick={onDelete} busy={!!deleting} disabled={busy || !!deleting} title="Delete scene" />
                    </span>
                )}
            </>}
        />
    );
};

// ============================================================================
// Publish status dropdown
// ============================================================================

const PUBLISH_SELECT_STYLE: React.CSSProperties = {
    fontSize: '11px', padding: '3px 6px', borderRadius: '3px',
    border: '1px solid var(--colorNeutralStroke1)',
    backgroundColor: 'var(--colorNeutralBackground3)',
    color: 'var(--colorNeutralForeground1)',
    cursor: 'pointer',
};

/** Dropdown for selecting publish/visibility state. */
export const PublishStatusSelect: React.FC<{
    value: string | null | undefined;
    onChange: (next: PublishStatus) => void;
    busy?: boolean;
    disabled?: boolean;
    title?: string;
}> = ({ value, onChange, busy, disabled, title }) => {
    const current = normalizeStatus(value);
    return (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--colorNeutralForeground3)' }} title={title}>
            <span>Access</span>
            <select
                value={current}
                disabled={disabled || busy}
                onChange={e => onChange(e.target.value as PublishStatus)}
                style={{ ...PUBLISH_SELECT_STYLE, opacity: busy ? 0.5 : 1 }}
            >
                {PUBLISH_STATUS_ORDER.map(s => (
                    <option key={s} value={s}>{PUBLISH_STATUS_LABELS[s]}</option>
                ))}
            </select>
            {busy && (
                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8z" opacity=".2"/>
                    <path d="M8 0a8 8 0 018 8h-1.5A6.5 6.5 0 008 1.5V0z"/>
                </svg>
            )}
        </label>
    );
};

// ============================================================================
// Confirmation bars
// ============================================================================

/** Confirmation bar shown when the user tries to open a scene while one is already loaded. */
export const ConfirmOpenBar: React.FC<{
    name: string;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ name, onConfirm, onCancel }) => (
    <div style={{
        padding: '8px', backgroundColor: 'var(--colorNeutralBackground2)',
        borderRadius: '4px', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px',
        border: '1px solid var(--colorNeutralStroke1)',
    }}>
        <span style={{ flex: 1, fontSize: '11px', color: 'var(--colorNeutralForeground1)' }}>
            Open &ldquo;{name}&rdquo;? This will replace the current scene.
        </span>
        <button
            style={{
                padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
                borderRadius: '3px', cursor: 'pointer',
                backgroundColor: 'var(--colorBrandBackground)', color: 'var(--colorNeutralForegroundOnBrand)',
            }}
            onClick={onConfirm}
        >
            Open
        </button>
        <button
            style={{
                padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
                borderRadius: '3px', cursor: 'pointer',
                backgroundColor: 'var(--colorNeutralBackground3)', color: 'var(--colorNeutralForeground1)',
            }}
            onClick={onCancel}
        >
            Cancel
        </button>
    </div>
);

/** Red confirmation bar for irreversible deletes (assets, scenes, projects). */
export const ConfirmDeleteBar: React.FC<{
    name: string;
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    /** Optional extra warning appended after the standard message. */
    detail?: string;
}> = ({ name, busy, onConfirm, onCancel, detail }) => (
    <div style={{
        padding: '8px', backgroundColor: 'var(--colorPaletteRedBackground1)',
        borderRadius: '4px', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px',
    }}>
        <span style={{ flex: 1, fontSize: '11px', color: 'var(--colorPaletteRedForeground1)' }}>
            Delete &ldquo;{name}&rdquo;? This cannot be undone.{detail ? ` ${detail}` : ''}
        </span>
        <button
            style={{
                padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
                borderRadius: '3px', cursor: 'pointer',
                backgroundColor: 'var(--colorPaletteRedBackground3)', color: '#fff',
                opacity: busy ? 0.5 : 1,
            }}
            onClick={onConfirm}
            disabled={busy}
        >
            {busy ? 'Deleting...' : 'Confirm'}
        </button>
        <button
            style={{
                padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
                borderRadius: '3px', cursor: 'pointer',
                backgroundColor: 'var(--colorNeutralBackground3)', color: 'var(--colorNeutralForeground1)',
                opacity: busy ? 0.5 : 1,
            }}
            onClick={onCancel}
            disabled={busy}
        >
            Cancel
        </button>
    </div>
);

// ============================================================================
// Create Scene form
// ============================================================================

/**
 * Inline form shown above the scene grid for creating a new scene.
 * Captures name (required) and an optional image marker (file + width in meters).
 */
export const CreateSceneForm: React.FC<{
    busy: boolean;
    onCreate: (params: { name: string; image: File | null; imageWidth: number | null }) => void;
    onCancel: () => void;
}> = ({ busy, onCreate, onCancel }) => {
    const [name, setName] = useState('');
    const [image, setImage] = useState<File | null>(null);
    const [imageWidth, setImageWidth] = useState('0.2'); // meters; sane default

    const canSubmit = name.trim().length > 0 && !busy;
    const widthNum = parseFloat(imageWidth);
    const widthValid = !image || (Number.isFinite(widthNum) && widthNum > 0);

    const submit = () => {
        if (!canSubmit || !widthValid) return;
        onCreate({
            name: name.trim(),
            image,
            imageWidth: image ? widthNum : null,
        });
    };

    return (
        <div style={{
            padding: '8px', backgroundColor: 'var(--colorNeutralBackground2)',
            borderRadius: '4px', margin: '0 0 6px',
            border: '1px solid var(--colorNeutralStroke1)',
            display: 'flex', flexDirection: 'column', gap: '6px',
        }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--colorNeutralForeground1)' }}>
                New scene
            </div>
            <input
                type="text"
                placeholder="Scene name"
                value={name}
                onChange={e => setName(e.target.value)}
                autoFocus
                disabled={busy}
                onKeyDown={e => { if (e.key === 'Enter') submit(); else if (e.key === 'Escape') onCancel(); }}
                style={styles.input}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--colorNeutralForeground2)' }}>
                <span style={{ minWidth: '72px' }}>Image marker</span>
                <input
                    type="file"
                    accept="image/*"
                    disabled={busy}
                    onChange={e => setImage(e.target.files?.[0] ?? null)}
                    style={{ flex: 1, fontSize: '11px', color: 'var(--colorNeutralForeground1)' }}
                />
            </label>
            {image && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: 'var(--colorNeutralForeground2)' }}>
                    <span style={{ minWidth: '72px' }}>Width (m)</span>
                    <input
                        type="number"
                        min="0.001"
                        step="0.01"
                        value={imageWidth}
                        onChange={e => setImageWidth(e.target.value)}
                        disabled={busy}
                        style={{ ...styles.input, flex: 1 }}
                    />
                </label>
            )}
            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button
                    style={{
                        padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
                        borderRadius: '3px', cursor: busy ? 'default' : 'pointer',
                        backgroundColor: 'var(--colorNeutralBackground3)', color: 'var(--colorNeutralForeground1)',
                        opacity: busy ? 0.6 : 1,
                    }}
                    onClick={onCancel}
                    disabled={busy}
                >
                    Cancel
                </button>
                <button
                    style={{
                        padding: '3px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
                        borderRadius: '3px', cursor: (canSubmit && widthValid) ? 'pointer' : 'default',
                        backgroundColor: 'var(--colorBrandBackground)', color: 'var(--colorNeutralForegroundOnBrand)',
                        opacity: (canSubmit && widthValid) ? 1 : 0.6,
                    }}
                    onClick={submit}
                    disabled={!canSubmit || !widthValid}
                >
                    {busy ? 'Creating...' : 'Create'}
                </button>
            </div>
        </div>
    );
};

// ============================================================================
// Login Screen
// ============================================================================

export const LoginScreen: React.FC<{ onLogin: (token: string, email: string) => void }> = ({ onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            if (!res.ok) {
                throw new Error(res.status === 401 ? 'Invalid email or password' : `Login failed (${res.status})`);
            }
            const data = await res.json() as { access_token?: string; accessToken?: string; refresh_token?: string; refreshToken?: string };
            const token = data.access_token ?? data.accessToken;
            if (!token) {
                throw new Error('No access token received');
            }
            const refreshToken = data.refresh_token ?? data.refreshToken;
            if (refreshToken) {
                localStorage.setItem(LS_REFRESH_TOKEN_KEY, refreshToken);
            }
            localStorage.setItem(LS_EMAIL_KEY, email);
            onLogin(token, email);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={{ ...styles.center, flexDirection: 'column', height: '100%' }}>
                <div style={{ marginBottom: '16px', color: 'var(--colorNeutralForeground3)' }}>Sign in to Trace Studio</div>
                <form onSubmit={handleSubmit} style={styles.loginForm}>
                    <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        style={styles.input}
                        autoComplete="email"
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        style={styles.input}
                        autoComplete="current-password"
                    />
                    {error && <div style={styles.loginError}>{error}</div>}
                    <button
                        type="submit"
                        style={{ ...styles.loginButton, opacity: loading ? 0.6 : 1 }}
                        disabled={loading || !email || !password}
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
};
